import { FilterBar } from '@/components/filters/FilterBar';
import { SearchInput } from '@/components/filters/SearchInput';
import { DateRangePicker } from '@/components/filters/DateRangePicker';
import { Stat } from '@/components/ui/Stat';
import {
  useAdminExecutions,
  useAutomationEffectAnomalies,
  useAutomationStats,
} from '@/hooks/useAdminAutomations';
import { formatNumber, formatPercentage } from '@/utils/formatters';
import { Cpu, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useState } from 'react';
import { ExecutionsTable } from '../components/ExecutionsTable';
import { AutomationStatsChart } from '../components/AutomationStatsChart';
import { FailedExecutionsAlert } from '../components/FailedExecutionsAlert';
import { EffectAnomaliesAlert } from '../components/EffectAnomaliesAlert';

interface AutomationStatsPayload {
  data?: AutomationStatsPayload;
  executions?: {
    running?: number;
    pending?: number;
    total?: number;
    successRate?: number;
    successRate30d?: number;
    failed24h?: number;
    effectAnomalies?: number;
  };
}

export function AutomationsMonitorPage() {
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);

  const { data, isLoading } = useAdminExecutions({
    search,
    limit: 25,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });
  const executions = data?.data || [];

  const { data: rawStats } = useAutomationStats('7d');
  const typedStats = rawStats as AutomationStatsPayload | undefined;
  const statsRoot = typedStats?.data ?? typedStats;
  const execStats = statsRoot?.executions ?? {};
  const running = execStats.running ?? 0;
  const pending = execStats.pending ?? 0;
  const successRate =
    (execStats.total ?? 0) > 0 ? (execStats.successRate ?? 0) : (execStats.successRate30d ?? 100);
  const failed24h = execStats.failed24h ?? 0;
  const effectAnomaliesCount = execStats.effectAnomalies ?? 0;
  const { data: effectAnomalies } = useAutomationEffectAnomalies(10);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
          Automatisations
        </h1>
        <p className="text-sm text-neutral-500">Suivi des exécutions en temps réel</p>
      </div>

      <FailedExecutionsAlert count={failed24h ?? 0} />
      <EffectAnomaliesAlert count={effectAnomaliesCount} anomalies={effectAnomalies} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="En cours" value={formatNumber(running)} icon={<Cpu className="h-5 w-5" />} />
        <Stat
          label="En attente"
          value={formatNumber(pending)}
          icon={<Clock className="h-5 w-5" />}
        />
        <Stat
          label="Taux de succès"
          value={formatPercentage(successRate)}
          icon={<CheckCircle className="h-5 w-5" />}
        />
        <Stat
          label="Échecs (24h)"
          value={formatNumber(failed24h)}
          icon={<XCircle className="h-5 w-5" />}
        />
      </div>

      <AutomationStatsChart />

      <div>
        <h2 className="mb-3 text-sm font-semibold text-neutral-600 dark:text-neutral-300">
          Dernières exécutions
        </h2>
        <FilterBar>
          <div className="w-72">
            <SearchInput value={search} onChange={setSearch} placeholder="Rechercher..." />
          </div>
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={(f, t) => {
              setDateFrom(f);
              setDateTo(t);
            }}
          />
        </FilterBar>
      </div>

      <ExecutionsTable data={executions} loading={isLoading} />
    </div>
  );
}
